// ** Modules
import { getProviders } from 'next-auth/react';
import { serverSideTranslations } from 'next-i18next/pages/serverSideTranslations';

// ** Components
import DevErrorMode from '@/components/layout/DevErrorMode';
import HomepageRenderer from '@/components/strapi/HomepageRenderer';
import MaintenanceMode from '@/components/layout/MaintenanceMode';

// ** Apollo
import { homepageQuery } from '@/lib/utils/homepageDataUtils';
import { initializeApollo } from '@/lib/apollo-client';
import { discoveryPagesQuery } from '@/lib/utils/pageDataUtils';

// ** Utils
import { buildPathFromRelationLink } from '@/utils/ui/navigationUtils';

// ** Types
import type { Query, Homepage as HomepageType } from '@/types/graphql';
import type { AppContext } from '@/types/contextTypes';
import type { GraphQLError } from 'graphql';
import type { GetServerSideProps, GetServerSidePropsContext } from 'next';
import type { User } from 'next-auth';

type ApolloManualQueryResult<T> = {
	data: T;
	errors?: readonly GraphQLError[];
	error?: GraphQLError;
	loading: boolean;
	networkStatus: number;
};

// Dynamically extract the Provider type from getProviders
type Providers = Awaited<ReturnType<typeof getProviders>>;

type HomepageProps = {
	host: string;
	locale: string;
	homepage?: HomepageType | null;
	providers?: Providers;
	currentUser?: User | null; // Allow null to match HomepageRenderer
	devError?: { message: string; stack?: string } | null;
	newsBasePath?: string;
	eventsBasePath?: string;
};

const Homepage = ({
	host,
	locale,
	homepage,
	providers,
	currentUser = null,
	devError,
	newsBasePath,
	eventsBasePath
}: HomepageProps) => {
	const firstProvider = providers ? Object.values(providers)[0] : undefined;

	if (devError) {
		console.error(devError);
		return <DevErrorMode error={devError} slug="home" message="Wartungsarbeiten an der Startseite." />;
	}

	if (!homepage) {
		return (
			<MaintenanceMode message="Der Service ist vorübergehend nicht erreichbar. Bitte versuchen Sie es später erneut." />
		);
	}

	return (
		<HomepageRenderer
			providers={firstProvider as any}
			locale={locale}
			content={homepage}
			currentUser={currentUser as User}
			host={host}
			newsBasePath={newsBasePath}
			eventsBasePath={eventsBasePath}
		/>
	);
};

const getSafeErrorMessage = (error: unknown): string => {
	if (!error) return 'An unknown error occurred';

	// 1. Check for Apollo/GraphQL Errors
	if (typeof error === 'object' && 'graphQLErrors' in error) {
		const gqlErrors = (error as any).graphQLErrors;
		if (Array.isArray(gqlErrors) && gqlErrors.length > 0) {
			// Return the specific field mismatch/validation message
			return gqlErrors[0].message;
		}
	}

	// 2. Check for Network/Apollo Client Errors
	if (typeof error === 'object' && 'networkError' in error) {
		const networkError = (error as any).networkError;
		if (networkError?.result?.errors?.[0]?.message) {
			return networkError.result.errors[0].message;
		}
		if (networkError?.message) return networkError.message;
	}

	// 3. Fallback to standard Error object or string
	if (error instanceof Error) return error.message;
	if (typeof error === 'string') return error;

	return JSON.stringify(error);
};

export const getServerSideProps: GetServerSideProps = async (context: GetServerSidePropsContext) => {
	const isDev = process.env.NODE_ENV === 'development';
	const host = context.req.headers.host ?? '';
	const locale = context.locale ?? ('de' as AppContext['locale']);
	const providers = (await getProviders()) ?? {
		prototype: { id: 'prototype', name: 'Prototype', type: 'oauth', signinUrl: '', callbackUrl: '' }
	};

	const client = initializeApollo({ host });

	try {
		const { data: pagesResponse } = await client.query({
			query: discoveryPagesQuery,
			variables: {
				locale,
				filters: {
					or: [{ isNewsLandingPage: { eq: true } }, { isEventsLandingPage: { eq: true } }]
				}
			},
			fetchPolicy: 'no-cache'
		});

		const allPages = pagesResponse?.pages || [];
		const newsLanding = allPages.find((page) => page?.isNewsLandingPage === true);
		const eventsLanding = allPages.find((page) => page?.isEventsLandingPage === true);
		const newsBasePath = buildPathFromRelationLink(newsLanding);
		const eventsBasePath = buildPathFromRelationLink(eventsLanding);

		// --- FETCH HOMEPAGE DATA ---
		const {
			data: responseData,
			errors,
			error
		} = (await client.query({
			query: homepageQuery,
			variables: { locale },
			fetchPolicy: 'no-cache',
			errorPolicy: 'all'
		})) as ApolloManualQueryResult<Query>;

		// Prioritize the detailed GraphQL validation error
		if (errors && errors.length > 0) {
			throw new Error(errors[0].message);
		}

		// Fallback to the combined error object if errors array is empty
		if (error) {
			throw new Error(error.message);
		}

		const homepage = responseData?.homepage;

		if (!homepage) {
			throw new Error('HOMEPAGE_DATA_MISSING: Strapi returned no data for the homepage.');
		}

		return {
			props: {
				host,
				locale,
				homepage: homepage,
				providers: providers ?? {},
				metadata: { title: 'Home' },
				newsBasePath: newsBasePath ?? undefined,
				eventsBasePath: eventsBasePath ?? undefined,
				isPrivateRoute: false,
				...(await serverSideTranslations(locale, ['home', 'header', 'common']))
			}
		};
	} catch (error: unknown) {
		const gqlErrorMessage = getSafeErrorMessage(error);
		console.error('❌ [Homepage Server Error]:', gqlErrorMessage);

		if (isDev) {
			return {
				props: {
					host,
					locale,
					providers: providers ?? {},
					homepage: null,
					devError: {
						message: gqlErrorMessage,
						stack: error instanceof Error ? error.stack : undefined
					},
					isPrivateRoute: false,
					metadata: { title: 'Home' },
					...(await serverSideTranslations(locale, ['common']))
				}
			};
		}

		return {
			props: {
				host,
				locale,
				homepage: null,
				providers: providers ?? {},
				metadata: { title: 'Service Unavailable' },
				isPrivateRoute: false,
				...(await serverSideTranslations(locale, ['home', 'header', 'common']))
			}
		};
	}
};

export default Homepage;
